import { useCallback } from 'react'
import { useAuthStore } from '@/store/useAuthStore'
import type { AuthUser } from '@/shared/types'

/**
 * A requirement is an endpoint key as the backend lists it in who-am-i `permissions`,
 * e.g. 'GET /admin/booking/get-bookings-list'. A trailing '*' matches any key with that
 * prefix, e.g. 'GET /admin/booking/*' = "can read anything under bookings".
 * An array means "any of these".
 */
export type PermRequirement = string | string[] | undefined

export function hasPermission(held: string[], required: string | string[]): boolean {
  const list = Array.isArray(required) ? required : [required]
  return list.some(r =>
    r.endsWith('*') ? held.some(h => h.startsWith(r.slice(0, -1))) : held.includes(r),
  )
}

/**
 * Whether `user` may see/do something. Deliberately fails OPEN where the user isn't governed by
 * this list, so nobody is locked out by a missing field:
 *  - no requirement            → allowed
 *  - not a platform admin      → allowed (school admins/collectors have their own rules)
 *  - super admin               → allowed
 *  - permissions not loaded    → allowed (who-am-i hasn't answered yet)
 * The backend still enforces every endpoint; this only decides what the UI shows.
 */
export function canUser(user: AuthUser | null | undefined, required: PermRequirement): boolean {
  if (!required || (Array.isArray(required) && required.length === 0)) return true
  if (!user) return false
  if (user.user_type !== 'admin') return true
  if (user.is_super_admin) return true
  if (!user.permissions) return true
  return hasPermission(user.permissions, required)
}

/** Permission names attached to a school admin's roles (e.g. 'booking_school_admin'). */
export function schoolPermissionNames(user: AuthUser | null | undefined): string[] {
  return (user?.school_admin_roles ?? [])
    .flatMap(r => r.role?.permissions?.map(p => p.name) ?? [])
    .filter(Boolean)
}

/**
 * Same rule as the school sidebar: a school admin sees something only if one of their roles holds
 * the permission. Fails OPEN for everyone else, and for a school admin with no permissions attached
 * at all (nothing configured yet), so nobody gets an empty dashboard.
 */
export function canSchoolUser(user: AuthUser | null | undefined, required: string | string[] | undefined): boolean {
  if (!required || (Array.isArray(required) && required.length === 0)) return true
  if (user?.user_type !== 'school_admin') return true
  const held = schoolPermissionNames(user)
  if (held.length === 0) return true
  return hasPermission(held, required)
}

/** `const schoolCan = useSchoolCan(); schoolCan('booking_school_admin')` */
export function useSchoolCan() {
  const user = useAuthStore(s => s.user)
  return useCallback((required: string | string[] | undefined) => canSchoolUser(user, required), [user])
}

/** `const can = useCan(); can('POST /admin/package/*')` */
export function useCan() {
  const user = useAuthStore(s => s.user)
  return useCallback((required: PermRequirement) => canUser(user, required), [user])
}
